// Named export: see the note at the top of LibraryFolder.js — the default
// export of src/store is the store FACTORY.
import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import { deepFreeze, timestampToDate } from "../util/misc";
import { isEmpty } from "lodash";
import { diff } from "deep-object-diff";

import FileRef from "./FileRef";
import {
  LIBRARY_TIERS,
  LIBRARY_TIER_KEYS,
  canAccessFile,
  canWriteFile,
} from "./LibraryFolder";

export const LIBRARY_FILE_FIELDS = {
  name: "",
  folderID: null,
  tier: "public",
  extension: "",
  size: 0,
  type: "",
  storagePath: "",
  url: "",
  description: "",
  // Meta info
  created: null,
  createdBy: null,
  modified: null,
  modifiedBy: null,
  // Soft-delete: when set, the file has been moved to Trash and is scheduled
  // for permanent deletion by the scheduled purge function after a retention
  // period (30 days).
  deletedAt: null,
  deletedBy: null,
};
deepFreeze(LIBRARY_FILE_FIELDS);

export default class LibraryFile extends DataStructure {
  constructor(id, data = {}) {
    data = sanitize(data, LIBRARY_FILE_FIELDS);

    data.created = timestampToDate(data.created);
    data.modified = timestampToDate(data.modified);
    data.deletedAt = timestampToDate(data.deletedAt);

    if (!LIBRARY_TIER_KEYS.includes(data.tier)) {
      data.tier = "public";
    }

    if (!data.extension && data.name) {
      const idx = data.name.lastIndexOf(".");
      if (idx >= 0) {
        data.extension = data.name.slice(idx + 1).toLowerCase();
      }
    }

    super(data);

    this.id = id || null;
    this.permissions = Object.freeze({
      create: LibraryFile.canBeCreatedBy,
      edit: this.canBeEditedBy,
      delete: this.canBeDeletedBy,
    });
  }

  // ------------------------------------------------------------------
  // Permissions
  // ------------------------------------------------------------------

  static canBeCreatedBy(user) {
    return Boolean(user && user.isEmployee);
  }

  // Rename / move / trash. A file inside a read-only folder can only be
  // changed by an administrator.
  canBeEditedBy(user) {
    if (!canWriteFile(user, this)) return false;
    return !this.isReadOnly || Boolean(user.isAdmin);
  }

  canBeDeletedBy(user) {
    if (this.isDeleted) return false;
    return this.canBeEditedBy(user);
  }

  canBeRestoredBy(user) {
    if (!this.isDeleted || !user) return false;
    // Personal-tier files can only ever be touched by their owner.
    if (this.data.tier === "personal") {
      return user.id === this.data.createdBy;
    }
    // Admins may restore anything; the original deleter may restore their
    // own — unless the folder has since become read-only.
    if (user.isAdmin) return true;
    return user.id === this.data.deletedBy && !this.isReadOnly;
  }

  canBePurgedBy(user) {
    if (!user || !this.isDeleted) return false;
    // Personal-tier owners may purge their own files immediately; otherwise
    // permanent deletion is admin-only and the scheduled purge runs as an
    // admin service account.
    if (this.data.tier === "personal") {
      return user.id === this.data.createdBy;
    }
    return Boolean(user.isAdmin);
  }

  canBeViewedBy(user) {
    return canAccessFile(user, this);
  }

  get isDeleted() {
    return Boolean(this.data.deletedAt);
  }

  // A file is read-only when its folder is (see LibraryFolder.isReadOnly).
  // An orphan — its folder deleted while the file sat in Trash — is not.
  get isReadOnly() {
    const folder = this.folder;
    return folder ? folder.isReadOnly : false;
  }

  // ------------------------------------------------------------------
  // Database
  // ------------------------------------------------------------------

  get path() {
    return LibraryFile.getPath(this.id);
  }

  static getPath(id) {
    let path = "libraryFiles";
    if (id) {
      path += `/${id}`;
    }
    return path;
  }

  static toFirestore(file) {
    return file.data;
  }

  static fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    return new LibraryFile(snapshot.id, data);
  }

  // ------------------------------------------------------------------
  // Presentation
  // ------------------------------------------------------------------

  get folder() {
    return this.data.folderID
      ? store.state.main.libraryFolders[this.data.folderID] || null
      : null;
  }

  // The file's folder was deleted (or never resolved): it belongs at the
  // root of its tier until someone moves it.
  get isOrphan() {
    return !this.folder;
  }

  get tierInfo() {
    return LIBRARY_TIERS[this.data.tier] || LIBRARY_TIERS.public;
  }

  get icon() {
    return FileRef.getIcon(this.data.extension);
  }

  get formattedSize() {
    return FileRef.getSize(this.data.size);
  }

  get isImage() {
    return FileRef.isImage({
      type: this.data.type,
      extension: this.data.extension,
      name: this.data.name,
    });
  }

  get isPdf() {
    return (
      this.data.extension === "pdf" ||
      String(this.data.type || "").toLowerCase() === "application/pdf"
    );
  }

  get isPreviewable() {
    return this.isPdf || (this.isImage && !FileRef.isHeif(this.data));
  }

  // The app's own viewer dialog for this file (src/router/routes/general.js),
  // or null when it has no preview and should be downloaded instead.
  get previewDialog() {
    if (!this.data.url) return null;
    const params = { src: this.data.url, title: this.data.name };
    if (this.isPdf) return { name: "pdf", params };
    if (this.isImage && this.isPreviewable) return { name: "image", params };
    return null;
  }

  get modifiedDate() {
    return this.data.modified || this.data.created || null;
  }

  get createdBy() {
    return this.data.createdBy
      ? store.state.main.users[this.data.createdBy] || null
      : null;
  }

  get modifiedBy() {
    return this.data.modifiedBy
      ? store.state.main.users[this.data.modifiedBy] || null
      : null;
  }

  get deletedByUser() {
    return this.data.deletedBy
      ? store.state.main.users[this.data.deletedBy] || null
      : null;
  }

  // ------------------------------------------------------------------
  // Validation
  // ------------------------------------------------------------------

  get rules() {
    return {
      name: [
        (a) => Boolean(a && a.trim().length) || "required",
        (a) => !/[\\/]/.test(a || "") || "fileNameInvalid",
      ],
      tier: [(a) => LIBRARY_TIER_KEYS.includes(a) || "required"],
    };
  }

  get isValid() {
    return Object.entries(this.rules).every(([key, rules]) =>
      rules.every((rule) => rule(this.data[key]) === true)
    );
  }

  get isEmpty() {
    return Boolean(!this.id && isEmpty(diff(this.data, LIBRARY_FILE_FIELDS)));
  }

  isEqual(other) {
    return Boolean(
      other && this.id === other.id && isEmpty(diff(this.data, other.data))
    );
  }

  toString() {
    return this.data.name;
  }
}
